// ABOUTME: Screen 1A Take a Pause: the Too Much petals around a breathing circle, with a duration picker and timed rounds.
// ABOUTME: Everything is local until the child is ready; a round draws its own clearing weather and saves in the background, ready sends the rest.

import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useCrossingContent } from '../../../hooks/useCrossingContent';
import TooMuchQuadrant from '../TooMuchQuadrant';
import BreathScene from '../crossing/BreathScene';
import { useCrossingReducedMotion } from '../crossing/useReducedMotion';
import type { ScreenProps, ScreenResult } from '../runner/screenContract';
import BreathingStage from './breath/BreathingStage';
import BreathPlateLine from './breath/BreathPlateLine';
import DurationPicker from './breath/DurationPicker';
import { useBreathingRound } from './breath/useBreathingRound';
import { useBreathCue } from './breath/useBreathCue';
import { useOpeningSequence, isFirstArrival } from './breath/useOpeningSequence';
import { useSwellPlayback } from './breath/useSwellPlayback';
import { picksToSend, picksFromStored, focusFromStored, picksDiffer } from './breath/tooMuchPicks';

const READY_LABEL = 'Ready for compass';
// Ready for compass (TRANS 4): the slices fade for 0.5 s, the sails fold for 1.6 s, then the scene zooms to the deck for 1.6 s.
const SLICE_FADE_MS = 500;
const FOLD_MS = 1600;
const ZOOM_MS = 1600;
// The headlines for a running and for a finished breathing round.
const BREATHING_HEADLINE = 'Breathe in. Breathe out.';
const BREATHING_DONE_HEADLINE = 'Nice, steady breathing.';

// Natural-language join for the "Okay — x, y, and z first." copy.
function joinPicks(picks: string[]): string {
  if (picks.length === 1) return picks[0];
  if (picks.length === 2) return `${picks[0]} and ${picks[1]}`;
  return `${picks.slice(0, -1).join(', ')}, and ${picks[picks.length - 1]}`;
}

export default function Screen1A({ crossing, runner }: ScreenProps): ScreenResult {
  const { data: content } = useCrossingContent();
  const [focused, setFocused] = useState<string | null>(() => focusFromStored(crossing.tooMuchCategories));
  // Every category's picks persist independently of which one is focused.
  const [categoryPicks, setCategoryPicks] = useState<Record<string, string[]>>(() => picksFromStored(crossing.tooMuchCategories));
  // The opening plays on a first arrival only: decided once at mount, so a save that stores something does not change it.
  const [firstArrival] = useState(() => isFirstArrival(crossing));
  const opening = useOpeningSequence(firstArrival);
  const skipOpening = opening.skip;
  const reducedMotion = useCrossingReducedMotion();
  const round = useBreathingRound({
    seedSeconds: crossing.breathSecondsTotal,
    seedRounds: crossing.breathRoundCount,
    onRoundComplete: totals => runner.run('log_breath_round', { ...totals }),
    reducedMotion,
  });

  const armed = round.phase === 'running' || round.phase === 'done';
  const cue = useBreathCue({ armed, reducedMotion, swellDur: round.swellDur, roundKey: round.roundKey });
  const backBandRef = useRef<HTMLElement | null>(null);
  const frontBandRef = useRef<HTMLElement | null>(null);
  useSwellPlayback({
    refs: [cue.canoeRef, backBandRef], frontRef: frontBandRef, mode: reducedMotion ? 'still' : armed ? 'round' : 'idle', elapsedMs: round.elapsedMs, total: round.total, reducedMotion,
  });

  // Ready for compass is a transition the screen plays before 2A is built: the slices fade, the sails fold and the scene zooms.
  const [leaving, setLeaving] = useState<{ slicesFading: boolean; zoom: boolean } | null>(null);
  const leavingTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const releaseHoldRef = useRef<(() => void) | null>(null);
  const didFurlRef = useRef(false);
  const sawSavingRef = useRef(false);
  const clearLeavingTimers = () => {
    leavingTimersRef.current.forEach(clearTimeout);
    leavingTimersRef.current = [];
    releaseHoldRef.current?.();
    releaseHoldRef.current = null;
  };
  // Leaving the screen ends the transition; a runner still holding its answer for it is let go.
  useEffect(() => clearLeavingTimers, []);

  // If the save fails, the runner stays on this screen with its message: put 1A back as it was before the press.
  // On success the runner adopts 2A in the render in which saving falls, so this screen is gone before it runs.
  useEffect(() => {
    if (!leaving) {
      sawSavingRef.current = false;
      return;
    }
    if (runner.saving) {
      sawSavingRef.current = true;
      return;
    }
    if (!sawSavingRef.current) return;
    clearLeavingTimers();
    setLeaving(null);
    skipOpening();
    if (didFurlRef.current) round.unfurl();
    didFurlRef.current = false;
  }, [runner.saving, leaving, round, skipOpening]);

  // Petal tap: the same code unfocuses, a different code replaces; never additive.
  const tapPetal = (code: string) => setFocused(previous => (previous === code ? null : code));

  // Pill tap: toggles that pill within the focused category.
  const tapPill = (label: string) => {
    if (!focused) return; // pills only render inside a focused petal
    setCategoryPicks(previous => {
      const current = previous[focused] ?? [];
      const updated = current.includes(label) ? current.filter(l => l !== label) : [...current, label];
      return { ...previous, [focused]: updated };
    });
  };

  const sent = picksToSend(categoryPicks, focused);
  const values = { tooMuchCategories: sent, ...round.totalsNow() };

  const ready = () => {
    if (leaving) return;
    opening.cancelRing();
    const needFurl = round.sails === 'set';
    didFurlRef.current = needFurl;
    if (reducedMotion) {
      // No zoom: the sails are furled at once and the answer is adopted as soon as it arrives.
      if (needFurl) round.furl();
      setLeaving({ slicesFading: false, zoom: false });
      runner.run('read_the_moment', values);
      return;
    }
    // The slices are only on the ring before Breathing strategy; from the mask or a round they are already gone.
    const slicesUp = round.phase === 'petals';
    const furlAt = slicesUp ? SLICE_FADE_MS : 0;
    const zoomAt = furlAt + (needFurl ? FOLD_MS : 0);
    const holdAdopt = new Promise<void>(resolve => {
      releaseHoldRef.current = resolve;
      leavingTimersRef.current.push(setTimeout(resolve, zoomAt + ZOOM_MS));
    });
    setLeaving({ slicesFading: slicesUp, zoom: false });
    if (needFurl) leavingTimersRef.current.push(setTimeout(round.furl, furlAt));
    leavingTimersRef.current.push(setTimeout(() => setLeaving(previous => previous && { ...previous, zoom: true }), zoomAt));
    runner.run('read_the_moment', values, { holdAdopt });
  };

  const focusedContent = focused ? content?.tooMuchCategories?.[focused] : undefined;
  const focusedPicks = focused ? categoryPicks[focused] ?? [] : [];

  let headline: string;
  let sub: string | undefined;
  if (round.phase === 'running') {
    headline = BREATHING_HEADLINE;
    sub = 'Keep breathing as long as it helps.';
  } else if (round.phase === 'done') {
    headline = BREATHING_DONE_HEADLINE;
    sub = 'The weather cleared — breathe again, or head to the compass when you’re ready.';
  } else if (round.phase === 'entering' || round.phase === 'picker' || round.phase === 'starting') {
    headline = 'What would help first?';
  } else if (focusedPicks.length > 0) {
    headline = `Okay — ${joinPicks(focusedPicks).toLowerCase()} first.`;
    sub = 'Then finish with a breath, or head to the compass when you’re ready.';
  } else if (focusedContent) {
    headline = focusedContent.phrase ?? focusedContent.title;
    sub = 'Pick what might make it lighter — we can change it.';
  } else {
    headline = 'What would help first?';
    sub = 'Press what you need — breath in the middle, needs around it.';
  }

  const layer = (
    <>
      {/* The round's state lives here and the scene host draws nothing for 1A, so the Reset scene is this layer's for the whole
          life of the screen: the resting sky, the storm coming in, the round and the cleared sky are one canoe and one set of
          animations. It sits outside the clipped box below because its sky and sea reach past the host to fill the screen. */}
      <BreathScene
        x={round.sceneX}
        armed={armed}
        weatherOn={round.weather}
        intro={round.intro}
        opening={opening.sky}
        sails={round.sails}
        zoom={leaving?.zoom}
        swellDur={round.swellDur}
        skyKey={round.roundKey}
        canoeRef={cue.canoeRef}
        backBandRef={backBandRef}
        frontBandRef={frontBandRef}
        reducedMotion={reducedMotion}
      />
      <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <BreathingStage
          phase={round.phase}
          total={round.total}
          leftSec={round.leftSec}
          petals={round.phase === 'petals' || round.phase === 'entering' ? (
            <TooMuchQuadrant
              key={opening.ringKey}
              entrance={opening.ring}
              categories={content?.tooMuchCategories ?? {}}
              focusedCode={focused}
              pickedLabels={focusedPicks}
              onPetalTap={tapPetal}
              onPillTap={tapPill}
              fading={round.phase !== 'petals' || !!leaving?.slicesFading}
              fadeSeconds={leaving?.slicesFading ? SLICE_FADE_MS / 1000 : undefined}
            />
          ) : null}
        />
        {(round.phase === 'picker' || round.phase === 'starting') && (
          <DurationPicker onPick={round.start} leaving={round.phase === 'starting'} reducedMotion={reducedMotion} />
        )}
      </Box>
    </>
  );

  const localActionsFor = () => {
    // Before the first round the card offers the strategy; it is not pressable while the storm slides in or the mask is up.
    if (!round.hasRun) return [{ id: 'breathing_strategy', label: 'Breathing strategy', glyph: '▶', onPress: () => { opening.cancelRing(); round.openStrategy(); }, disabled: round.phase !== 'petals' }];
    // Once the round is done every plate button is filled, so the way on and the way back read alike.
    return [{ id: 'restart_round', label: round.lastRoundDone ? 'Breathe again' : 'Start over', glyph: '↻', onPress: round.openPicker, filled: round.phase === 'done' }];
  };

  return {
    layer,
    headline,
    sub,
    slots: { read_the_moment: { label: READY_LABEL, glyph: '✓', values, onPress: ready } },
    localActions: localActionsFor(),
    hasUnsentState: picksDiffer(sent, crossing.tooMuchCategories) || round.unsentSeconds,
    ...(armed && {
      phaseVariant: 'breathing_round' as const,
      headlineHidden: true,
      plateLine: <BreathPlateLine leftSec={round.leftSec} cueIn={cue.cueIn} reducedMotion={reducedMotion} />,
    }),
  };
}
